import { useQuery } from '@tanstack/react-query';
import { AnyActivityDetail } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  GetOracleActivityDetailParams,
  GetOracleActivityDetailResponse
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { OracleLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { ActivityId } from './activity-feed-data-utils';

export const oracleActivityQueryKey = (activityId: ActivityId): readonly unknown[] => [
  'oracleActivity',
  activityId
];

export interface OracleActivityResult {
  activity: AnyActivityDetail | undefined;
  isLoading: boolean;
  error: Error | null;
}

// Uses the activity's own cid, since the cross-client feed has no route cid. The key omits cid
// because the mutation hooks write to it by activityId alone. It includes foldedIds
// so a card whose group grows on a later page refetches the new members' comments.
export const useOracleActivity = (
  activityId: ActivityId,
  cid: string,
  foldedIds?: string[]
): OracleActivityResult => {
  const query = useQuery<AnyActivityDetail>({
    queryKey: [...oracleActivityQueryKey(activityId), foldedIds?.length ? foldedIds : undefined],
    queryFn: async () => {
      const params: GetOracleActivityDetailParams = {
        cid,
        ...(foldedIds?.length && { foldedIds: foldedIds.join(',') })
      };
      const res = await OracleLambdaFetch(`/events/${encodeURIComponent(activityId)}`, { params });
      const body = await awaitJSON<GetOracleActivityDetailResponse>(res);
      return body.activity;
    }
  });
  return {
    activity: query.data,
    isLoading: query.isLoading,
    error: query.error ?? null
  };
};
